import React, { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Check, Copy, Wand2 } from 'lucide-react';

/**
 * Génère un mot de passe et le donne à copier.
 *
 * Demandé deux fois dans la revue du 31/08 — écran super admin (« il faudrait ici avoir
 * la possibilité de générer un mot de passe automatiquement, de façon à pouvoir avoir
 * un mot de passe qui soit cohérent dès le départ ») et écran de création de poseur
 * (« il faut que je puisse générer un mot de passe. D'ailleurs c'est quelque chose à
 * mettre en place dans le reste de l'app »).
 *
 * UN SEUL COMPOSANT POUR LES TROIS ÉCRANS, et c'est le sens de la remarque : trois
 * générateurs écrits séparément produiraient trois politiques de mot de passe, dont la
 * plus faible ferait la sécurité de l'ensemble.
 *
 * `crypto.getRandomValues` et non `Math.random` : ce dernier est prévisible, et ce
 * mot de passe ouvre un compte. Le repli sur `Math.random` n'existe pas — un navigateur
 * sans `crypto` est un navigateur sur lequel on ne veut pas créer de compte.
 *
 * ⚠️ CE MOT DE PASSE NE PART JAMAIS PAR COURRIEL. `services/notifications_compte` le
 * dit : « un identifiant et un mot de passe réunis dans un même email font de la boîte
 * de réception un trousseau ». Il se transmet de vive voix ou par un canal séparé —
 * d'où le bouton « copier », qui est le geste réellement utile ici.
 */

// Ni I ni l ni O ni 0 : ce mot de passe se dicte au téléphone et se recopie à la main.
// Un caractère ambigu se paye en un appel au support, pas en sécurité gagnée.
const LETTRES = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
const CHIFFRES = '23456789';
const SIGNES = '!@#$%*?';
const ALPHABET = LETTRES + CHIFFRES + SIGNES;
const LONGUEUR = 16;

export function genererMotDePasse() {
  const tirage = new Uint32Array(LONGUEUR);
  crypto.getRandomValues(tirage);
  const brut = Array.from(tirage, (n) => ALPHABET[n % ALPHABET.length]);
  // On IMPOSE au moins un de chaque famille plutôt que de tirer jusqu'à en avoir :
  // une boucle de rejet peut, en théorie, ne pas se terminer.
  const impose = new Uint32Array(3);
  crypto.getRandomValues(impose);
  brut[0] = LETTRES[impose[0] % LETTRES.length];
  brut[1] = CHIFFRES[impose[1] % CHIFFRES.length];
  brut[2] = SIGNES[impose[2] % SIGNES.length];
  return brut.join('');
}

export default function BoutonMotDePasse({ valeur, onGenere, tid = 'mdp' }) {
  const [copie, setCopie] = useState(false);

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(valeur);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé, permission) : le mot de passe
      // reste lisible à l'écran, le geste n'est pas perdu. Rien à signaler.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button" variant="outline" size="sm" className="h-8 text-[12px]"
        onClick={() => onGenere(genererMotDePasse())}
        data-testid={`${tid}-generer`}
      >
        <Wand2 className="w-3.5 h-3.5 mr-1.5" />
        Générer
      </Button>
      {valeur ? (
        <Button
          type="button" variant="ghost" size="sm" className="h-8 text-[12px]"
          onClick={copier}
          data-testid={`${tid}-copier`}
        >
          {copie
            ? <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />Copié</>
            : <><Copy className="w-3.5 h-3.5 mr-1.5" />Copier</>}
        </Button>
      ) : null}
    </div>
  );
}
